import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { startWith } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';

import { SystemMessage } from 'app/core/system-message/system-message-service';
import { NotificationService } from 'app/notification.service';
import { RecaptchaService } from 'app/services/recaptcha.service';
import { validateForm } from 'app/shared/form-elements/form-validateForm.function';
import { PfsHelpTicketVm } from 'app/shared/generated/Models/Public/PfsHelpTicketVm';
import { SelectItem } from 'app/shared/models/select-item';
import { Router } from '@angular/router';

/**
 * Public "Register for an account in HOMER" page — the prettier, PrimeNG version of the account-request form
 * reached at /help-ticket?id=register. Mirrors the new internal help-desk ticket UI (card + bg-nav header,
 * floatlabel/iconfield inputs, filterable p-select, p-inputmask phone, centered button footer). Submits the
 * same PfsHelpTicketVm to the same public endpoint as before — only the presentation changed. Category is
 * fixed to "Account Registration" and priority defaults to High (both set server-facing, not shown).
 */
@Component({
    selector: 'pcg-public-register',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,

        ButtonModule,
        FloatLabelModule,
        IconFieldModule,
        InputIconModule,
        InputMaskModule,
        InputTextModule,
        SelectModule,
        TextareaModule,
    ],
    templateUrl: './public-register.component.html',
    styleUrl: './public-register.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicRegisterComponent {
    private readonly http = inject(HttpClient);
    private readonly ns = inject(NotificationService);
    private readonly recaptchaService = inject(RecaptchaService);
    readonly router = inject(Router);

    formGroup = PfsHelpTicketVm.Form;
    model = PfsHelpTicketVm.GetModel(this.formGroup);

    facilities = signal<SelectItem[]>([]);
    isSubmitting = signal<boolean>(false);
    isSuccess = signal<boolean>(false);

    /** Mirrors PfsHelpTicketVm.Description's MaxLength, for the textarea cap and the character counter. */
    readonly descriptionMaxLength = 500;

    /**
     * Reactive snapshot of the form value. The template's character counter and error text read this so
     * they keep up with typing under OnPush change detection.
     */
    private readonly formValue = toSignal(
        this.formGroup.valueChanges.pipe(startWith(this.formGroup.getRawValue())),
    );

    /** Characters entered in the Description, shown against the 500-character limit. */
    descriptionLength = computed(() => ((this.formValue()?.description as string) ?? '').length);

    /** Field labels, so a message names the field the way the form does. */
    private static readonly labels: Record<string, string> = {
        fullName: 'Name',
        email: 'Email',
        facilityId: 'Facility',
        phoneNumber: 'Phone Number',
        subject: 'Brief Subject',
        description: 'Description',
    };

    private readonly facilityDataSource = 'api/PublicHelpTicket/Facilities';
    private readonly categoryDataSource = 'api/PublicHelpTicket/HelpDeskCategories';
    private readonly submitEndpoint = 'api/PublicPfsHelpTicket/SubmitPfsHelpTicket';

    ngOnInit() {
        // Fixed server-facing values for a registration request (not shown in the UI).
        this.model.priorityId = 1;
        this.model.priorityName = 'High';
        this.model.categoryName = 'Account Registration';

        this.http.get<SelectItem[]>(this.facilityDataSource)
            .subscribe((items) => this.facilities.set(items ?? []));

        // Resolve the "Account Registration" category id.
        this.http.get<SelectItem[]>(this.categoryDataSource).subscribe((items) => {
            const categoryId = items?.find((o) => o.text === 'Account Registration')?.value;
            if (categoryId != null) {
                this.model.categoryId = categoryId as number;
                this.formGroup.controls.categoryId.setValue(categoryId);
            }
        });

        this.setUpHoneyPot();
        this.setUpFacilityRequired();
    }

    private setUpHoneyPot() {
        this.formGroup.controls['honeyPot'].setValidators(this.requireFalse());
        this.formGroup.updateValueAndValidity();
    }

    /**
     * facilityId starts at 0, and Angular's Validators.required treats 0 as present, so the generated
     * required validator alone lets an unselected facility through — and the server's [Required] on a
     * non-nullable int accepts it too, producing a registration request with no facility for support to
     * act on. Require an actual selection.
     */
    private setUpFacilityRequired() {
        this.formGroup.controls['facilityId'].addValidators(this.requireSelection());
        this.formGroup.controls['facilityId'].updateValueAndValidity();
    }

    private requireFalse(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null =>
            control.value === true ? { honeyPot: true } : null;
    }

    /** Reports the standard `required` key so the message reads "Facility is required." */
    private requireSelection(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null =>
            control.value > 0 ? null : { required: true };
    }

    /** Keep the facilityName in sync with the selected facility (used by the submission VM). */
    facilityChange() {
        const selected = this.facilities().find((o) => o.value === this.model.facilityId);
        this.formGroup.controls.facilityName.setValue(selected?.text ?? '');
    }

    /** Strip characters the ticket store rejects from the email, matching the legacy form's behavior. */
    sanitizeEmail() {
        const raw = (this.formGroup.controls.email.value as string) ?? '';
        const cleaned = raw
            .trim()
            .replace(/['&?:,;]/g, '')
            .replace('--', '')
            .replace('/', '')
            .replace(/NULL/g, '')
            .replace(/xp_/g, '')
            .replace(/[\n\r]+/g, '')
            .replace('\\', '');
        this.formGroup.controls.email.setValue(cleaned);
    }

    /**
     * The message to show under a control, chosen from the error key Angular actually reported. Without
     * this the form only turned the control red, so a rejected-format entry was indistinguishable from an
     * empty one — a populated Description looked like it was still "required". Returns null while the
     * control is untouched and clean, so nothing appears before the user has engaged with the field.
     */
    errorFor(name: string): string | null {
        const control = this.formGroup.get(name);
        if (!control || control.valid || !(control.touched || control.dirty)) { return null; }

        const label = PublicRegisterComponent.labels[name] ?? 'This field';
        const errors = control.errors ?? {};

        if (errors['required']) { return `${label} is required.`; }
        if (errors['maxlength']) {
            return `${label} must be ${errors['maxlength'].requiredLength} characters or fewer` +
                ` — you have ${errors['maxlength'].actualLength}.`;
        }
        if (errors['email']) { return 'Enter a valid email address, such as name@flhealth.gov.'; }
        if (errors['pattern']) {
            // The shared free-text pattern (PfsHelpTicketVm.FreeTextPattern) rejects exactly two things:
            // an entry that is nothing but whitespace, and one containing angle brackets.
            return `${(control.value as string) ?? ''}`.trim().length === 0
                ? `${label} is required.`
                : `${label} cannot contain < or >. Other punctuation is fine.`;
        }
        return `${label} is not valid.`;
    }

    onSubmit() {
        if (!this.formGroup.valid) {
            validateForm(this.formGroup);
            return;
        }

        this.recaptchaService.submit(
            {
                action: 'submitPfsHelpTicket',
                apiEndpoint: this.submitEndpoint,
                formGroup: this.formGroup,
                loadingSignal: this.isSubmitting,
            },
            {
                onSuccess: (msg: SystemMessage) => {
                    if (msg.isSuccessful) {
                        this.isSuccess.set(true);
                        this.formGroup.reset();
                    } else {
                        this.ns.showError(msg.message);
                    }
                },
                onCreateTokenError: () => {},
                onVerifyTokenError: () => {},
            },
        );
    }
}
